import React, { useEffect, useState } from 'react';
import { Trans } from '@common/i18n/trans';
import { Link } from 'react-router-dom';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import styles from './PublicTemplatesPage.module.css';
import { apiClient } from '@common/http/query-client';
import { Template } from './template';

export function PublicTemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);

  // Fetch public templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('templates', {
          params: { perPage: 24, public: true }, // Ensure we fetch public templates
        });

        const fetchedTemplates = response.data?.pagination?.data || [];
        if (fetchedTemplates.length > 0) {
          setTemplates(fetchedTemplates as Template[]);
        } else {
          console.warn('No public templates returned from API.');
        }
      } catch (err) {
        console.error('Failed to load templates', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Update filtered templates based on the search query
  useEffect(() => {
    const filtered = templates.filter((template) =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredTemplates(filtered);
  }, [searchQuery, templates]);

  return (
    <div className={styles.publicTemplatesPage}>
      <header className={styles.header}>
        <h1><Trans message="Explore Our Templates" /></h1>
        <TextField
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchBar}
        />
      </header>

      <div className={styles.templatesGrid}>
        {loading ? (
          <div className={styles.loadingMessage}>
            <Trans message="Loading templates..." />
          </div>
        ) : filteredTemplates.length > 0 ? (
          filteredTemplates.map((template) => (
            <div key={template.id} className={styles.templateCard}>
              <img
                className={styles.templateImage}
                src={template.thumbnail}
                alt={template.name}
              />
              <div className={styles.templateDetails}>
                <h3>{template.name}</h3>
                {template.description && (
                  <p className={styles.templateDescription}>
                    {template.description}
                  </p>
                )}
                <Link to={`/templates/${template.name}`}>
                  <Button variant="flat" color="primary">
                    <Trans message="Preview" />
                  </Button>
                </Link>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.noResults}>
            <Trans message="No templates found." />
          </div>
        )}
      </div>
    </div>
  );
}
